import React, { useEffect, useState } from "react";
import { getAccessToken } from "../../../storage/storage";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { IoMdArrowBack, IoMdClose } from "react-icons/io";
import { TableSkelton1 } from "../../../components/Skeletons";
import PageLoader from "../../../components/PageLoader";
import { useLocation, useNavigate } from "react-router-dom";
import { isVideoUrl, getVideoMimeType } from "../../../utils/mediaType";
import { getApiErrorMessage } from "../../../utils/apiError";

const TaskSheet = () => {
  const token = getAccessToken();
  const { id } = useParams();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const previousPath = location.state?.from || "/stays";
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/task-sheet/${id}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setInvoiceData(response.data[0]);
        setError("");
        setLoading(false);
      } catch (err) {
        console.error("Error fetching invoice:", err);
        setInvoiceData(null);
        setError(getApiErrorMessage(err, "Failed to load task sheet."));
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [id]);

  // Function to extract all media files from tasks
  const getAllMediaFiles = () => {
    if (!invoiceData?.tasks) return [];

    return invoiceData.tasks.reduce((acc, task) => {
      if (task.media_urls && task.media_urls.length > 0) {
        return [...acc, ...task.media_urls.map(url => ({
          url,
          taskName: task.task_name
        }))];
      }
      return acc;
    }, []);
  };

  const mediaFiles = getAllMediaFiles();

  const openPreview = (media) => {
    setSelectedMedia(media);
    setIsPreviewOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };
  const handleBackClick = () => {
    // If there's previous state, use it, otherwise go back in history
    if (location.state?.from) {
      navigate(previousPath);
    } else {
      navigate(-1); // Go back one step in history
    }
  };
  return (
    <div className={`${loading ? "h-[100vh]" : "mb-10 min-h-screen"}`}>
      {loading ? (
        <PageLoader />
      ) : (
        <div className="relative">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 absolute lg:top-2 top-[-30px] left-6 text-xl font-bold text-[#586989] hover:text-blue-600"
          >
            <IoMdArrowBack />
            <p>Back</p>
          </button>
          {invoiceData ? (
            <div className="p-6 min-h-screen">
              {/* Invoice Details */}
              <h2 className="text-4xl font-bold mb-4 mt-10 text-[#586989]">
                Task Sheet
              </h2>

              {/* Cleaner Details */}
              <div className="my-6">
                <p>
                  <strong>Cleaner name:</strong> {invoiceData?.cleaner?.name}
                </p>
              </div>

              {/* Property Details */}
              <div className="my-8">
                <h3 className="text-lg font-semibold ">Property:</h3>
                <div className="border border-[#DFDFDF] bg-white rounded-lg shadow-md overflow-x-auto w-full max-w-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#FFF5CE] text-[#586989]">
                        <th className="border border-gray-300 p-2"> Name</th>
                        <th className="border border-gray-300 p-2">Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData?.property ? (
                        <tr className="border-b">
                          <td className="border border-gray-300 p-5 text-center">
                            {invoiceData.property.name}
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            {invoiceData.property.address}
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td
                            colSpan="2"
                            className="text-center py-5 text-gray-500"
                          >
                            Property name and address are not available at the
                            moment."{" "}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tasks Table */}
              <h3 className="text-lg font-semibold">Tasks:</h3>
              <div className="">
                <div className="border border-[#DFDFDF] bg-white rounded-lg shadow-md overflow-x-auto w-full max-w-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#FFF5CE] text-[#586989]">
                        <th className="border border-gray-300 p-2">
                          Task Name
                        </th>
                        <th className="border border-gray-300 p-2">Quantity</th>
                        <th className="border border-gray-300 p-2">Rate (£)</th>
                        <th className="border border-gray-300 p-2">
                          Total (£)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData?.tasks?.map((task, index) => (
                        <tr key={index} className="border-b">
                          <td className="border border-gray-300 p-5 text-center">
                            {task?.task_name}
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            {task?.quantity}
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            £{task?.rate}
                          </td>
                          <td className="border border-gray-300 p-2 font-semibold text-center">
                            £{task?.total}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Comments Section */}
              <div className="flex items-start gap-3 p-4 bg-[#FFF5CE] rounded-lg border border-gray-300 shadow-sm mt-8">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#586989]">
                    Comments
                  </h3>
                  <div
                    // "prose prose-sm leading-tight max-w-none bg-white p-6 mt-3 rounded-lg border border-gray-300 shadow-sm [&_p]:my-1 [&_li]:my-1"
                    className="prose leading-tight max-w-none bg-white p-6 mt-3 rounded-lg border border-gray-300 shadow-sm [&_p]:my-1 [&_li]:my-1"
                    dangerouslySetInnerHTML={{
                      __html: (invoiceData?.comments || "No comments available")
                        .replace(/\n/g, "<br />"),
                    }}
                  />
                </div>
              </div>

              {/* Media Gallery Section */}
              {mediaFiles.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-[#586989] mb-4">
                    Media Files
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {mediaFiles.map((media, index) => {
                      const isVideo = isVideoUrl(media.url);

                      return (
                        <div
                          key={index}
                          className="bg-white rounded-lg border border-[#DFDFDF] shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => openPreview(media)}
                        >
                          <div className="p-4 bg-[#FFF5CE]">
                            <h4 className="font-medium text-[#586989]">
                              {media.taskName} ({isVideo ? 'Video' : 'Image'})
                            </h4>
                          </div>
                          <div className="p-4 flex justify-center items-center h-48">
                            {isVideo ? (
                              <video
                                controls
                                className="max-h-full max-w-full pointer-events-none"
                              >
                                <source src={media.url} type={getVideoMimeType(media.url)} />
                                Your browser does not support the video tag.
                              </video>
                            ) : (
                              <img
                                src={media.url}
                                alt={`Media for ${media.taskName}`}
                                className="max-h-full max-w-full object-contain pointer-events-none"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="p-6 h-[70vh] bg-white flex justify-center items-center text-[#586989] text-[24px] font-semibold overflow-y-auto">
                <p className="bg-white shadow-xl p-10 h-[400px] rounded-lg">
                  <div className="border border-[#106A3A] p-6 rounded-[8px] flex flex-col gap-10 h-full w-full items-center justify-center">
                    {error || "The cleaner has not yet submitted their task sheet."}
                  </div>
                </p>
              </div>
            </>
          )}

          {/* Media Preview Modal */}
          {isPreviewOpen && selectedMedia && (
            <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
              <button
                onClick={closePreview}
                className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 transition-colors"
              >
                <IoMdClose />
              </button>

              <div className="relative max-w-full max-h-full">
                {isVideoUrl(selectedMedia.url) ? (
                  <video
                    controls
                    autoPlay
                    className="max-h-[90vh] max-w-full"
                  >
                    <source src={selectedMedia.url} type={getVideoMimeType(selectedMedia.url)} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    src={selectedMedia.url}
                    alt={`Preview of ${selectedMedia.taskName}`}
                    className="max-h-[90vh] max-w-full object-contain"
                  />
                )}
                <div className="text-white text-center mt-2">
                  {selectedMedia.taskName}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskSheet;